import { useMemo, useState } from "react"
import { Check, ChevronsUpDown, X } from "lucide-react"

import { cn } from "../../utils/css"
import { Button } from "../shadcn/Button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../shadcn/Command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../shadcn/Popover"

export interface ComboBoxListOption {
  label: string;
  value: string;
  disabled?: boolean;
}

export interface ComboBoxProps {
  list: ComboBoxListOption[];
  noFilterMatchesLabel?: string;
  placeholder?: string;
  value: ComboBoxListOption | null | undefined;
  onSelect: (selected: ComboBoxListOption | null | undefined, isNew?: boolean) => void;
  isNewItemsAllowed?: boolean;
  inputValue: string
  setInputValue: (value: string) => void;
  shouldFilter?: boolean;
  className?: string;
  buttonClassName?: string;
  onScrollEnd?: () => void;
  isLoadingMore?: boolean;
}

const ComboBox = ({ list, noFilterMatchesLabel = "No matches", placeholder = "Select an option...", value, onSelect, inputValue, setInputValue, isNewItemsAllowed = false, shouldFilter = true, className, buttonClassName, onScrollEnd, isLoadingMore }: ComboBoxProps) => {
  const [open, setOpen] = useState(false)

  const selectedValueLabel = useMemo(() => value && value?.value
    ? list.find((item) => item.value === value.value)?.label : ""
    , [value, list])

  const isNoMatches = useMemo(() =>
    inputValue && shouldFilter ? !list.some((option) => option.value === inputValue) : list.length === 0
    , [inputValue, shouldFilter, list]);

  return (
    <div className={cn("flex items-center gap-1", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(`mw-[200px] justify-between ${selectedValueLabel ? "text-accent-foreground" : ""}`, buttonClassName)}
          >
            {selectedValueLabel || placeholder}
            <ChevronsUpDown className="opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="mw-[200px] p-0" style={{ zIndex: 9999 }}>
          <Command shouldFilter={shouldFilter}>
            <CommandInput placeholder={placeholder} onValueChange={(value) => setInputValue(value)} />
            <CommandList
              onScroll={(e) => {
                if (!onScrollEnd) return;
                const target = e.currentTarget;
                const isNearBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 50;
                if (isNearBottom && !isLoadingMore) {
                  onScrollEnd();
                }
              }}
            >
              {!isNewItemsAllowed && isNoMatches && <CommandEmpty>{noFilterMatchesLabel}</CommandEmpty>}
              <CommandGroup heading={isNoMatches ? '' : 'Suggestions'}>
                {list.map((item) => (
                  <CommandItem
                    key={item.value}
                    value={item.value}
                    disabled={item.disabled}
                    onSelect={(currentValue) => {
                      if (item.disabled) return;
                      const selected = list.find(({ value }) => value === currentValue)
                      onSelect(selected === value ? null : selected)
                      setOpen(false)
                    }}
                  >
                    {item.label}
                    <Check
                      className={cn(
                        "ml-auto",
                        value && value.value === item.value ? "opacity-100" : "opacity-0"
                      )}
                    />
                  </CommandItem>
                ))}
                {isLoadingMore && (
                  <CommandItem disabled value="loading">
                    Loading more...
                  </CommandItem>
                )}
              </CommandGroup>
              <CommandGroup forceMount>
                {isNewItemsAllowed && isNoMatches && (
                  <CommandItem forceMount value={inputValue} onSelect={(value) => {
                    onSelect({ label: value, value: value }, true);
                    setOpen(false);
                  }}>
                    <Check className="mr-2 h-4 w-4 opacity-0" />
                    Add "{inputValue}"
                  </CommandItem>)
                }
              </CommandGroup>

            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {value && value?.value && (
        <X
          className="cursor-pointer h-4 w-4 shrink-0 opacity-50"
          onClick={(e) => {
            onSelect(null)
          }}
          aria-label="Clear selection"
        />
      )}
    </div>
  )
}

export default ComboBox